'use client'
import { logger } from '@/lib/logger';

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import SWSWebGPUVisualizer from './SWSWebGPUVisualizer'
import SWSFallbackVisualizer from './SWSFallbackVisualizer'

// ============================================================================
// SOUND WIZARD STUDIO - UNIFIED VISUALIZER
// ============================================================================
// Optimized unified visualizer with performance monitoring and intelligent fallback
// Version: 2.1.0 - Enhanced with real-time performance metrics
// ============================================================================

interface SWSUnifiedVisualizerProps {
  readonly audioData?: Float32Array
  readonly neuralActivity?: number[]
  readonly frequencyData?: Float32Array
  readonly width: number
  readonly height: number
  readonly mode?: 'waveform' | 'spectrum' | 'neural' | 'particles' | 'spatial'
  readonly className?: string
}

interface PerformanceMetrics {
  readonly fps: number
  readonly frameTime: number
  readonly memoryUsage?: number
  readonly lastUpdate: number
}

// Memoized component with performance optimizations
const SWSUnifiedVisualizer: React.FC<SWSUnifiedVisualizerProps> = React.memo(({
  audioData,
  neuralActivity,
  frequencyData,
  width,
  height,
  mode = 'waveform',
  className = ''
}) => {
  // State management with optimized updates
  const [supportsWebGPU, setSupportsWebGPU] = useState<boolean | null>(null)
  const [webGPUFailed, setWebGPUFailed] = useState(false)
  const [currentMode, setCurrentMode] = useState(mode)
  const [performanceMetrics, setPerformanceMetrics] = useState<PerformanceMetrics>({
    fps: 0,
    frameTime: 0,
    lastUpdate: Date.now()
  })
  const [error, setError] = useState<string | null>(null)

  // Refs for performance monitoring
  const webGPUCheckRef = useRef<Promise<boolean> | null>(null)
  const frameCountRef = useRef(0)
  const lastFrameTimeRef = useRef(performance.now())
  const metricsIntervalRef = useRef<NodeJS.Timeout | null>(null)

  // Optimized WebGPU detection with better error handling
  const checkWebGPUSupport = useCallback(async (): Promise<boolean> => {
    try {
      if (!navigator.gpu) {
        logger.log('WebGPU not available in navigator')
        return false
      }

      logger.log('Requesting WebGPU adapter...')
      const adapter = await navigator.gpu.requestAdapter()
      if (!adapter) {
        logger.log('No WebGPU adapter found')
        return false
      }

      // Check for required features
      const requiredFeatures: GPUFeatureName[] = []
      const device = await adapter.requestDevice({
        requiredFeatures,
        requiredLimits: {
          maxTextureDimension2D: 4096,
          maxBufferSize: 256 * 1024 * 1024, // 256MB
        }
      })

      device.destroy() // Clean up test device
      logger.log('WebGPU initialization successful')
      return true
    } catch (error) {
      logger.warn('WebGPU support check failed:', error)
      return false
    }
  }, [])

  // WebGPU initialization with timeout and error handling
  useEffect(() => {
    if (webGPUCheckRef.current === null) {
      webGPUCheckRef.current = Promise.race([
        checkWebGPUSupport(),
        new Promise<boolean>((_, reject) =>
          setTimeout(() => reject(new Error('WebGPU check timeout')), 3000)
        )
      ])
    }

    webGPUCheckRef.current
      .then((supported) => {
        setSupportsWebGPU(supported)
        setError(null)
      })
      .catch((error) => {
        logger.warn('WebGPU check failed or timed out:', error)
        setSupportsWebGPU(false)
        setError(error instanceof Error ? error.message : 'WebGPU initialization failed')
      })
  }, [checkWebGPUSupport])

  // Performance monitoring
  useEffect(() => {
    const updateMetrics = () => {
      const now = performance.now()
      const deltaTime = now - lastFrameTimeRef.current
      frameCountRef.current++

      if (deltaTime >= 1000) { // Update every second
        const fps = (frameCountRef.current * 1000) / deltaTime
        const frameTime = deltaTime / frameCountRef.current

        setPerformanceMetrics({
          fps: Math.round(fps),
          frameTime: Math.round(frameTime * 100) / 100,
          memoryUsage: (performance as any).memory?.usedJSHeapSize,
          lastUpdate: Date.now()
        })

        frameCountRef.current = 0
        lastFrameTimeRef.current = now
      }
    }

    metricsIntervalRef.current = setInterval(updateMetrics, 100)

    return () => {
      if (metricsIntervalRef.current) {
        clearInterval(metricsIntervalRef.current)
      }
    }
  }, [])

  // Update current mode when mode prop changes
  useEffect(() => {
    setCurrentMode(mode)
  }, [mode])

  // Error handler for WebGPU failures
  const handleWebGPUError = useCallback(() => {
    setWebGPUFailed(true)
    setError('WebGPU rendering failed, switching to Canvas2D fallback')
  }, [])

  // Memoized performance display component
  const PerformanceDisplay = React.memo(() => (
    <div className="mt-2 space-y-2">
      {/* Main performance indicator */}
      <div className="text-center">
        <div className={`inline-flex items-center space-x-2 px-3 py-1 rounded-full text-xs ${
          useWebGPU
            ? 'bg-green-500/20 border border-green-500/30 text-green-400'
            : 'bg-blue-500/20 border border-blue-500/30 text-blue-400'
        }`}>
          <div className={`w-1.5 h-1.5 rounded-full ${
            useWebGPU ? 'bg-green-400' : 'bg-blue-400'
          }`}></div>
          <span>
            {useWebGPU ? 'WebGPU Accelerated' : 'Canvas2D Rendering'}
          </span>
        </div>
      </div>

      {/* Detailed performance metrics */}
      <div className="text-center">
        <div className="inline-flex items-center space-x-4 px-3 py-1 rounded-lg bg-slate-900/50 border border-slate-700/50 text-xs text-cyan-400/60">
          <span>FPS: {performanceMetrics.fps}</span>
          <span>Frame: {performanceMetrics.frameTime}ms</span>
          {performanceMetrics.memoryUsage && (
            <span>Mem: {Math.round(performanceMetrics.memoryUsage / 1024 / 1024)}MB</span>
          )}
        </div>
      </div>

      {/* Error display */}
      {error && (
        <div className="text-center">
          <div className="inline-block px-3 py-1 rounded-lg bg-red-500/20 border border-red-500/30 text-red-400 text-xs">
            ⚠️ {error}
          </div>
        </div>
      )}

      {/* WebGPU fallback notice */}
      {supportsWebGPU === false && (
        <div className="text-center">
          <div className="inline-block px-3 py-1 rounded-lg bg-yellow-500/20 border border-yellow-500/30 text-yellow-400 text-xs">
            💡 WebGPU not supported - using Canvas2D fallback for optimal performance
          </div>
        </div>
      )}
    </div>
  ))

  PerformanceDisplay.displayName = 'PerformanceDisplay'

  // Show loading state while checking WebGPU support
  if (supportsWebGPU === null) {
    return (
      <div className={`sws-unified-visualizer ${className}`}>
        <div className="bg-black/90 backdrop-blur-md rounded-xl p-8 border border-cyan-500/30">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-400 mb-4"></div>
            <h3 className="text-lg font-bold text-cyan-400 mb-2">Checking GPU Support...</h3>
            <p className="text-sm text-cyan-300/60">Detecting WebGPU capabilities</p>
          </div>
        </div>
      </div>
    )
  }

  // Use WebGPU if supported and not failed, otherwise use Canvas2D
  const useWebGPU = supportsWebGPU && !webGPUFailed

  return (
    <div className={`sws-unified-visualizer ${className}`}>
      {useWebGPU ? (
        <SWSWebGPUVisualizer
          audioData={audioData}
          neuralActivity={neuralActivity}
          frequencyData={frequencyData}
          width={width}
          height={height}
          mode={currentMode}
          className={className}
        />
      ) : (
        <SWSFallbackVisualizer
          audioData={audioData}
          neuralActivity={neuralActivity}
          frequencyData={frequencyData}
          width={width}
          height={height}
          mode={currentMode}
          className={className}
        />
      )}

      {/* Enhanced Performance Display */}
      <PerformanceDisplay />
    </div>
  )
})


SWSUnifiedVisualizer.displayName = 'SWSUnifiedVisualizer'

export default SWSUnifiedVisualizer;
